
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { getLevelConfig } from '../utils/levelConfig';
import './victory.css';


function useAudioCtx() {
  const ctxRef = useRef(null);
  const getCtx = () => {
    if (!ctxRef.current)
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return ctxRef.current;
  };
  const playTone = useCallback((freq, type = 'sine', duration = 0.12, volume = 0.18) => {
    try {
      const ctx = getCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = type; osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + duration);
    } catch (_) {}
  }, []);
  const playHover = useCallback(() => playTone(880, 'sine', 0.07, 0.08), [playTone]);
  const playClick = useCallback(() => playTone(440, 'triangle', 0.14, 0.22), [playTone]);
  const playFanfare = useCallback(() => {
    [523, 659, 784, 1047, 1318].forEach((f, i) =>
      setTimeout(() => playTone(f, 'sine', 0.3, 0.22), i * 110)
    );
  }, [playTone]);
  const playCoin = useCallback(() => playTone(1200, 'square', 0.06, 0.1), [playTone]);
  return { playHover, playClick, playFanfare, playCoin };
}

/* ── CONFETTI PARTICLES ── */
const CONFETTI_COLORS = ['#B5E917', '#18B8E8', '#FF0078', '#FFD930', '#66F7D2'];
const PARTICLE_COUNT = 28;

const Confetti = () => {
  const particles = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    delay: Math.random() * 1.2,
    duration: 2.4 + Math.random() * 1.6,
    size: 6 + Math.random() * 8,
    rotateDir: Math.random() > 0.5 ? 1 : -1,
  }));

  return (
    <div className="victory-confetti">
      {particles.map(p => (
        <span
          key={p.id}
          className="victory-confetti-piece"
          style={{
            left: `${p.left}%`,
            background: p.color,
            width: p.size,
            height: p.size * 0.6,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            '--rot': p.rotateDir,
          }}
        />
      ))}
    </div>
  );
};


const Victory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { playHover, playClick, playFanfare, playCoin } = useAudioCtx();

  /* data passed from the level (with safe fallbacks) */
  const {
    level = 1,
    stars = 3,
    coins = 50,
    replayPath = `/level${level}`,
  } = location.state || {};

const nextLevel = level + 1;
const nextLevelConfig = getLevelConfig(nextLevel);
const nextPath = nextLevelConfig ? nextLevelConfig.path : '/levels';

  const [displayedCoins, setDisplayedCoins] = useState(0);
  const [starsRevealed, setStarsRevealed] = useState(0);

  const cardRef = useRef(null);
  const trophyRef = useRef(null);
  const starRefs = useRef([]);


  useEffect(() => {
    playFanfare();

    const tl = gsap.timeline();

    tl.fromTo(cardRef.current,
      { opacity: 0, y: 40, scale: 0.9 },
      { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(1.4)' }
    );

    tl.fromTo(trophyRef.current,
      { opacity: 0, scale: 0.4, rotate: -15 },
      { opacity: 1, scale: 1, rotate: 0, duration: 0.6, ease: 'back.out(2)' },
      '-=0.3'
    );

    starRefs.current.forEach((el, i) => {
      tl.fromTo(el,
        { opacity: 0, scale: 0, rotate: -180 },
        {
          opacity: 1, scale: 1, rotate: 0, duration: 0.45, ease: 'back.out(3)',
          onStart: () => { if (i < stars) { playCoin(); setStarsRevealed(s => s + 1); } },
        },
        '-=0.15'
      );
    });


    tl.call(() => {
      let current = 0;
      const step = Math.max(1, Math.ceil(coins / 40));
      const interval = setInterval(() => {
        current += step;
        if (current >= coins) {
          current = coins;
          clearInterval(interval);
        }
        setDisplayedCoins(current);
      }, 25);
    });

    return () => tl.kill();

  }, []);

  const handleReplay = () => { playClick(); navigate(replayPath); };
  const handleNext   = () => { playClick(); navigate(nextPath); };
  const handleLevels = () => { playClick(); navigate('/levels'); };

  return (
    <main className="victory">
      <Confetti />

      <section className="victory-card" ref={cardRef}>
        <div className="victory-trophy" ref={trophyRef}>🏆</div>

        <h1 className="victory-title">LEVEL COMPLETE!</h1>
        <h4 className="victory-sub">Level {level} Cleared</h4>


        <div className="victory-stars-row">
          {[0, 1, 2].map(i => (
            <span
              key={i}
              ref={el => (starRefs.current[i] = el)}
              className={`victory-star ${i < stars ? 'earned' : 'unearned'}`}
            >
              ★
            </span>
          ))}
        </div>


        <div className="victory-coins-row">
          <div className="victory-coin-icon" />
          <span className="victory-coins-count">+{displayedCoins}</span>
        </div>


        <div className="victory-btn-row">
          <button className="victory-btn victory-btn--ghost" onClick={handleReplay} onMouseEnter={playHover}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>
            </svg>
            REPLAY
          </button>

          <button className="victory-btn victory-btn--solid" onClick={handleNext} onMouseEnter={playHover}>
            NEXT LEVEL
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>

          <button className="victory-btn victory-btn--ghost" onClick={handleLevels} onMouseEnter={playHover}>
            BACK TO LEVELS
          </button>
        </div>
      </section>
    </main>
  );
};

export default Victory;